import React, { useCallback, useEffect, useState } from "react";
import { Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "../../lib/siteInfo";
import { LaunchCard, StatusBadge, inputClass, inputStyle } from "./ui";

function CodePanel() {
  const [code, setCode] = useState(null);
  const create = async () => {
    try { const { data } = await api.post("/admin/site-link/code"); setCode(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid="site-link-code-panel">
      <div className="eyebrow mb-2">1 · On one site</div>
      <p className="text-sm mb-3" style={{ color: "var(--muted)" }}>Show a one-time pairing code (valid 15 minutes).</p>
      {code ? (
        <div className="text-sm">
          <div className="font-mono text-2xl font-bold tracking-widest" style={{ color: "var(--ink)" }} data-testid="site-link-code">{code.code}</div>
          <div className="mt-1 text-xs" style={{ color: "var(--muted)" }}>This site: <span className="font-mono" data-testid="site-link-this-site">{code.this_site}</span> · expires {formatWhen(code.expires_at)}</div>
        </div>
      ) : (
        <button type="button" className="btn-ghost" onClick={create} data-testid="site-link-create-code-btn">Show pairing code</button>
      )}
    </div>
  );
}

function PairPanel({ onPaired }) {
  const [url, setUrl] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const pair = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/site-link/pair", { peer_url: url, code });
      toast.success(`Linked with ${data.peer_url}`);
      onPaired();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid="site-link-pair-panel">
      <div className="eyebrow mb-2">2 · On the other site</div>
      <label className="field-label">Address of the site showing the code</label>
      <input className={inputClass} style={inputStyle} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://prod-portal.myemployeesfirst.com" data-testid="site-link-peer-url-input" />
      <label className="field-label mt-3">Pairing code</label>
      <input className={inputClass} style={inputStyle} value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-1234" data-testid="site-link-code-input" />
      <button type="button" className="btn-primary mt-3" disabled={busy || !url || code.length < 6} onClick={pair} data-testid="site-link-pair-btn">
        {busy ? "Linking…" : "Link sites"}
      </button>
    </div>
  );
}

export function LinkedSiteCard() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/site-link").then(({ data: d }) => setData(d))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async () => {
    if (!window.confirm("Revoke the link? Release Control stops comparing the two sites until they are paired again.")) return;
    try { await api.delete("/admin/site-link"); toast.success("Link revoked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return null;
  return (
    <LaunchCard eyebrow="Linked site" title={data.linked ? "Connected for release comparison" : "Connect dev-portal and production"} testId="linked-site-card"
      aside={data.linked ? <StatusBadge status={data.reachable ? "pass" : "fail"} label={data.reachable ? "Reachable" : "Unreachable"} testId="site-link-status-badge" /> : null}>
      <p className="text-sm mb-4 max-w-3xl" style={{ color: "var(--muted)" }}>
        A read-only link that carries release information only — version, page and feature switch states and release notes. No client data, passwords or files. Either site can revoke it.
      </p>
      {data.linked ? (
        <div className="text-sm" data-testid="site-link-details">
          <div className="flex items-center gap-2 font-semibold" style={{ color: "var(--ink)" }}>
            <Link2 size={14} /> <span className="font-mono" data-testid="site-link-peer-url">{data.peer_url}</span>
          </div>
          <div className="mt-1" style={{ color: "var(--muted)" }}>
            <span data-testid="site-link-peer-env">{data.peer_environment || "unknown"}</span>
            {data.peer_version && <> · <span data-testid="site-link-peer-version">v{data.peer_version}</span></>}
            {" "}· paired {formatWhen(data.paired_at)}{data.paired_by ? ` by ${data.paired_by}` : ""}
          </div>
          {data.error && <div className="mt-2 text-xs" style={{ color: "#842029" }} data-testid="site-link-error">{data.error}</div>}
          <button type="button" className="btn-ghost mt-4 flex items-center gap-2" onClick={revoke} data-testid="site-link-revoke-btn"><Unlink size={14} /> Revoke link</button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2"><CodePanel /><PairPanel onPaired={load} /></div>
      )}
    </LaunchCard>
  );
}
